import React, { useState, useEffect } from "react";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { toastify } from "../../../../utils/toast";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { getItem } from "../../../../utils/localStorage";
import { terminate_lease, get_all_handovers } from "../../../../utils/urls";
import { useNavigate } from "react-router-dom";

const TerminateLease = ({ leaseId, initialStatus, fetchLeases, tenantId, unitId }) => {
    const navigate = useNavigate();
    const [status, setStatus] = useState(initialStatus);
    const [isLoading, setIsLoading] = useState(false);
    const [isCheckingExisting, setIsCheckingExisting] = useState(false);
    const [confirmDialogVisible, setConfirmDialogVisible] = useState(false);
    const [successDialogVisible, setSuccessDialogVisible] = useState(false);
    const [errorDialogVisible, setErrorDialogVisible] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [handoverData, setHandoverData] = useState(null);
    const [existingHandoverId, setExistingHandoverId] = useState(null);

    useEffect(() => {
        setStatus(initialStatus);
    }, [initialStatus]);

    const canTerminate = (currentStatus) => {
        // Can only terminate active leases
        return currentStatus === 'Active';
    };

    // Check if a move-out handover already exists for this tenant and unit
    const checkExistingHandover = async () => {
        try {
            setIsCheckingExisting(true);
            const facilityId = await getItem("selectedFacilityId");
            
            if (!facilityId) {
                throw new Error("Facility ID not found. Please select a facility first.");
            }

            // Get all handovers and filter for move-out handovers for this tenant and unit
            const response = await makeRequest2(
                `${get_all_handovers}/${facilityId}`,
                "GET"
            );

            if (response && response.success && response.data) {
                let handovers = [];
                
                // Handle different response formats
                if (Array.isArray(response.data)) {
                    handovers = response.data;
                } else if (response.data.handovers && Array.isArray(response.data.handovers)) {
                    handovers = response.data.handovers;
                } else if (typeof response.data === 'object') {
                    // Try to find arrays in the response
                    Object.keys(response.data).forEach(key => {
                        if (Array.isArray(response.data[key])) {
                            handovers = [...handovers, ...response.data[key]];
                        }
                    });
                }

                // Filter for move-out handovers for this tenant and unit
                const existingMoveOutHandovers = handovers.filter(handover => {
                    const handoverTenantId = typeof handover.customerId === 'object' 
                        ? handover.customerId._id 
                        : handover.customerId;
                        
                    const handoverUnitId = typeof handover.unitId === 'object'
                        ? handover.unitId._id
                        : handover.unitId;
                        
                    return (
                        handover.handoverType === 'MoveOut' && 
                        handoverTenantId === tenantId &&
                        handoverUnitId === unitId
                    );
                });

                if (existingMoveOutHandovers.length > 0) {
                    // Found existing handover
                    const existingHandover = existingMoveOutHandovers[0];
                    setExistingHandoverId(existingHandover._id);
                    
                    // Show error if there's a draft handover
                    if (existingHandover.status === 'Draft') {
                        setErrorMessage("A draft move-out handover already exists for this tenant and unit. Please complete or delete it before terminating the lease.");
                        setErrorDialogVisible(true);
                        return false;
                    }
                    
                    // If completed handover exists, we can proceed but will show a notice
                    if (existingHandover.status === 'Completed') {
                        return true;
                    }
                }
                
                // No blocking handovers found
                return true;
            } else {
                throw new Error("Failed to check for existing handovers");
            }
        } catch (error) {
            console.error("Error checking existing handovers:", error);
            toastify("Error checking existing handovers: " + error.message, "error");
            return false;
        } finally {
            setIsCheckingExisting(false);
        }
    };

    const handleShowConfirmDialog = async () => {
        if (!canTerminate(status)) {
            toastify(
                status === 'Expired' 
                    ? "Cannot terminate an expired lease" 
                    : "Lease cannot be terminated in its current status",
                "error"
            );
            return;
        }
        
        // Check for existing handovers before showing confirmation
        const canProceed = await checkExistingHandover();
        if (canProceed) {
            setConfirmDialogVisible(true);
        }
    };

    const handleTerminateLease = async () => {
        try {
            if (!canTerminate(status)) {
                toastify(
                    status === 'Expired' 
                        ? "Cannot terminate an expired lease" 
                        : "Lease cannot be terminated in its current status",
                    "error"
                );
                return;
            }

            setIsLoading(true);
            const facilityId = await getItem("selectedFacilityId");
            if (!facilityId) {
                throw new Error("Facility ID not found. Please select a facility first.");
            }

            console.log(`Attempting to terminate lease ${leaseId} for facility ${facilityId}`);

            const response = await makeRequest2(
                `${terminate_lease}/${facilityId}/${leaseId}`,
                "POST",
                {}
            );

            console.log("Terminate lease response:", response);

            if (response && response.success) {
                setStatus('Terminated');
                
                // Extract handover data from the response - using the updated structure
                const handoverInfo = response.data?.handover || {};
                const handoverDetails = response.data?.handoverDetails || {};
                
                // Update handover state with real data from the response
                setHandoverData({
                    leaseId: leaseId,
                    unitId: handoverInfo.unitId || unitId,
                    unitName: handoverInfo.unitName || "Unknown Unit",
                    tenantId: handoverInfo.customerId || tenantId,
                    tenantName: handoverInfo.tenantName || "Unknown Tenant",
                    handoverId: handoverInfo.handoverId || handoverInfo._id,
                    status: handoverInfo.status || "Draft",
                    createdDate: handoverInfo.handoverDate ? new Date(handoverInfo.handoverDate).toLocaleDateString() : new Date().toLocaleDateString(),
                    details: handoverDetails
                });
                
                toastify("Lease terminated successfully", "success");
                
                // Close confirm dialog and show success dialog with handover info
                setConfirmDialogVisible(false);
                setSuccessDialogVisible(true);
                
                // Auto close the success dialog after 10 seconds
                setTimeout(() => {
                    setSuccessDialogVisible(false);
                }, 10000);
                
                // Refresh lease list
                await fetchLeases();
            } else {
                throw new Error(response?.error || "Failed to terminate lease.");
            }
        } catch (err) {
            console.error("Error terminating lease:", err);
            toastify(err.message || "An error occurred while terminating the lease.", "error");
        } finally {
            setIsLoading(false);
        }
    };

    const handleViewHandover = () => {
        if (handoverData && handoverData.handoverId) {
            // Navigate to the handover details page
            navigate("/facility/handover_management/handovers", {
                state: { 
                    activeTab: 'moveOut',
                    highlightHandoverId: handoverData.handoverId
                }
            });
        } else {
            // Fallback to the handovers list
            navigate("/facility/handover_management/handovers", { 
                state: { activeTab: 'moveOut' } 
            });
        }
        setSuccessDialogVisible(false);
    };

    const handleEditHandover = () => {
        if (handoverData && handoverData.handoverId) {
            // Navigate to edit the handover
            navigate(`/facility/handover_management/add_move_out_handover`, {
                state: { 
                    handoverId: handoverData.handoverId,
                    unitId: handoverData.unitId,
                    customerId: handoverData.tenantId
                }
            });
        }
        setSuccessDialogVisible(false);
    };

    const handleEditExistingHandover = () => {
        // Navigate to the existing handover
        if (existingHandoverId) {
            navigate(`/facility/handover_management/handovers`, {
                state: { activeTab: 'moveOut', highlightHandoverId: existingHandoverId }
            });
        }
        setErrorDialogVisible(false);
    };

    // Confirmation Dialog Footer
    const confirmFooter = (
        <div>
            <Button
                label="Cancel"
                icon="ti ti-x"
                className="p-button-text"
                onClick={() => setConfirmDialogVisible(false)}
                disabled={isLoading}
            />
            <Button
                label="Terminate Lease"
                icon="ti ti-lock"
                className="p-button-danger"
                onClick={handleTerminateLease}
                loading={isLoading}
            />
        </div>
    );

    // Success Dialog Footer with Edit and View options
    const successFooter = (
        <div>
            <Button
                label="Close"
                icon="ti ti-x"
                className="p-button-text"
                onClick={() => setSuccessDialogVisible(false)}
            />
            <Button
                label="Edit Handover"
                icon="ti ti-pencil"
                className="p-button-warning me-2"
                onClick={handleEditHandover}
            />
            <Button
                label="View Handover"
                icon="ti ti-clipboard-check"
                className="p-button-success"
                onClick={handleViewHandover}
            />
        </div>
    );

    // Error Dialog Footer
    const errorFooter = (
        <div>
            <Button
                label="Close"
                icon="ti ti-x"
                className="p-button-text"
                onClick={() => setErrorDialogVisible(false)}
            />
            <Button
                label="View Existing Handover"
                icon="ti ti-clipboard-list"
                className="p-button-primary"
                onClick={handleEditExistingHandover}
            />
        </div>
    );

    return (
        <>
            <Button
                icon="ti ti-lock"
                className={`p-button-rounded ${
                    status === "Terminated" ? "p-button-danger" : "p-button-warning"
                } mr-2`}
                onClick={handleShowConfirmDialog}
                disabled={isLoading || isCheckingExisting || !canTerminate(status)}
                loading={isCheckingExisting}
                tooltip={
                    status === "Terminated"
                        ? "Terminated"
                        : !canTerminate(status)
                            ? `Cannot terminate ${status.toLowerCase()} lease`
                            : "Terminate Lease"
                }
            />

            {/* Confirmation Dialog */}
            <Dialog
                header="Confirm Lease Termination"
                visible={confirmDialogVisible}
                style={{ width: '450px' }}
                modal
                footer={confirmFooter}
                onHide={() => !isLoading && setConfirmDialogVisible(false)}
                closable={!isLoading}
            >
                <div className="confirmation-content d-flex align-items-center">
                    <i className="ti ti-alert-triangle me-3" style={{ fontSize: '2rem', color: '#ff9800' }} />
                    <span>
                        Are you sure you want to terminate this lease? This action cannot be undone and may have 
                        legal and financial implications.
                    </span>
                </div>
            </Dialog>

            {/* Success Dialog with Handover Info */}
            <Dialog
                header="Lease Terminated Successfully"
                visible={successDialogVisible}
                style={{ width: '500px' }}
                modal
                footer={successFooter}
                onHide={() => setSuccessDialogVisible(false)}
                closeOnEscape={false}
                dismissableMask={false}
            >
                <div className="success-content">
                    <div className="p-3 text-center mb-4 bg-light rounded">
                        <i className="ti ti-clipboard-check mb-2" style={{ fontSize: '3rem', color: '#2E7D32' }}></i>
                        <h4 className="mt-2 mb-0">Draft Move-Out Handover Created</h4>
                        <p className="text-muted mb-0">Automatic handover created for terminated lease</p>
                    </div>

                    {handoverData && (
                        <div className="p-3">
                            <h5 className="mb-3">Handover Details</h5>
                            <ul className="list-group mb-3">
                                <li className="list-group-item d-flex justify-content-between align-items-center">
                                    <span>Unit:</span>
                                    <strong>{handoverData.unitName}</strong>
                                </li>
                                <li className="list-group-item d-flex justify-content-between align-items-center">
                                    <span>Tenant:</span>
                                    <strong>{handoverData.tenantName}</strong>
                                </li>
                                <li className="list-group-item d-flex justify-content-between align-items-center">
                                    <span>Status:</span>
                                    <span className="badge bg-warning">Draft</span>
                                </li>
                                <li className="list-group-item d-flex justify-content-between align-items-center">
                                    <span>Created Date:</span>
                                    <strong>{handoverData.createdDate}</strong>
                                </li>
                                <li className="list-group-item d-flex justify-content-between align-items-center">
                                    <span>Handover ID:</span>
                                    <strong>{handoverData.handoverId}</strong>
                                </li>
                                {handoverData.details && handoverData.details.securityDeposit && (
                                    <li className="list-group-item d-flex justify-content-between align-items-center">
                                        <span>Security Deposit:</span>
                                        <strong>${handoverData.details.securityDeposit.toFixed(2)}</strong>
                                    </li>
                                )}
                                {handoverData.details && handoverData.details.itemCount > 0 && (
                                    <li className="list-group-item d-flex justify-content-between align-items-center">
                                        <span>Inventory Items:</span>
                                        <strong>{handoverData.details.itemCount}</strong>
                                    </li>
                                )}
                            </ul>
                            
                            <div className="alert alert-info">
                                <i className="ti ti-info-circle me-2"></i>
                                Complete the handover process to document property condition and process the security deposit.
                            </div>
                        </div>
                    )}
                    
                    <span className="auto-close-text text-muted" style={{ fontSize: '0.8rem', position: 'absolute', bottom: '10px', right: '10px' }}>
                        This dialog will close automatically in a few seconds...
                    </span>
                </div>
            </Dialog>

            {/* Error Dialog for existing handover */}
            <Dialog
                header="Existing Handover Found"
                visible={errorDialogVisible}
                style={{ width: '500px' }}
                modal
                footer={errorFooter}
                onHide={() => setErrorDialogVisible(false)}
            >
                <div className="error-content">
                    <div className="p-3 text-center mb-4 bg-light rounded">
                        <i className="ti ti-alert-triangle mb-2" style={{ fontSize: '3rem', color: '#F44336' }}></i>
                        <h4 className="mt-2 mb-0">Cannot Terminate Lease</h4>
                    </div>
                    
                    <div className="p-3">
                        <div className="alert alert-danger">
                            <i className="ti ti-info-circle me-2"></i>
                            {errorMessage}
                        </div>
                    </div>
                </div>
            </Dialog>
        </>
    );
};

export default TerminateLease;